import { createClient } from '@supabase/supabase-js';
import { Database, UserBusinessInfo, Customer, CustomerInsert, CustomerUpdate, CustomModule, CustomModuleInsert, CustomModuleUpdate, FormField, FormFieldInsert, FormFieldUpdate, FormData, FormDataInsert, FormDataUpdate, Role, RoleInsert, RoleUpdate, UserRole, UserRoleInsert, PDFTemplate, PDFTemplateInsert, PDFTemplateUpdate, PublicForm, PublicFormInsert, PublicFormUpdate, Notification, NotificationInsert, NotificationUpdate, Employee, EmployeeInsert, EmployeeUpdate, Department, DepartmentInsert, DepartmentUpdate, Transaction, TransactionInsert, TransactionUpdate, Invoice, InvoiceInsert, InvoiceUpdate, Product, ProductInsert, ProductUpdate, Supplier, SupplierInsert, SupplierUpdate, Sale, SaleInsert, SaleUpdate, PayrollRecord, PayrollRecordInsert, PayrollRecordUpdate, LeaveRequest, LeaveRequestInsert, LeaveRequestUpdate } from '../types/database';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables. Please check your .env file.');
}

export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true
  }
});

// Auth helper functions
export const auth = {
  signUp: async (email: string, password: string, userData?: { name?: string; company?: string }) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: userData
      }
    });
    return { data, error };
  },

  signIn: async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    return { data, error };
  },

  signOut: async () => {
    const { error } = await supabase.auth.signOut();
    return { error };
  },

  getCurrentUser: async () => {
    const { data: { user }, error } = await supabase.auth.getUser();
    return { user, error };
  },

  getSession: async () => {
    const { data: { session }, error } = await supabase.auth.getSession();
    return { session, error };
  }
};

// Business helper functions
export const businessService = {
  createBusiness: async (name: string, industry?: string) => {
    const { data, error } = await supabase.rpc('create_business_with_admin', {
      business_name: name,
      business_industry: industry
    });
    return { data, error };
  },

  getBusinessById: async (businessId: string) => {
    const { data, error } = await supabase
      .from('businesses')
      .select('*')
      .eq('id', businessId)
      .single();
    
    return { data, error };
  },

  updateBusiness: async (businessId: string, updates: { name?: string; industry?: string }) => {
    const { data, error } = await supabase
      .from('businesses')
      .update(updates)
      .eq('id', businessId)
      .select()
      .single();
    
    return { data, error };
  },

  getBusinessUsers: async (businessId: string) => {
    // First get the memberships
    const { data: memberships, error: membershipError } = await supabase
      .from('user_business_memberships')
      .select('*')
      .eq('business_id', businessId)
      .order('created_at', { ascending: false });

    if (membershipError) {
      return { data: null, error: membershipError };
    }

    if (!memberships || memberships.length === 0) {
      return { data: [], error: null };
    }

    // Get user IDs from memberships
    const userIds = memberships.map(m => m.user_id);

    // Get user profiles for these users
    const { data: profiles, error: profileError } = await supabase
      .from('user_profiles')
      .select('*')
      .in('id', userIds);

    if (profileError) {
      return { data: null, error: profileError };
    }

    // Get user emails from auth.users (this requires a custom function or RPC)
    // For now, we'll create a combined result without emails
    const combinedData = memberships.map(membership => {
      const profile = profiles?.find(p => p.id === membership.user_id);
      return {
        ...membership,
        user_profile: profile || null,
        user: {
          email: '' // We'll need to get this from auth.users via RPC or custom function
        }
      };
    });

    return { data: combinedData, error: null };
  },

  getUserBusinesses: async (userId?: string) => {
    const { data, error } = await supabase.rpc('get_user_businesses', {
      p_user_id: userId
    });
    
    return { data: data as UserBusinessInfo[] | null, error };
  }
};

// User profile helper functions
export const userProfileService = {
  getCurrentProfile: async () => {
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', (await supabase.auth.getUser()).data.user?.id)
      .single();
    
    return { data, error };
  },

  updateProfile: async (updates: { full_name?: string }) => {
    const { data, error } = await supabase
      .from('user_profiles')
      .update(updates)
      .eq('id', (await supabase.auth.getUser()).data.user?.id)
      .select()
      .single();
    
    return { data, error };
  }
};

// User business membership helper functions
export const userBusinessMembershipService = {
  updateRole: async (userId: string, businessId: string, role: 'admin' | 'manager' | 'staff') => {
    const { data, error } = await supabase
      .from('user_business_memberships')
      .update({ role, updated_at: new Date().toISOString() })
      .eq('user_id', userId)
      .eq('business_id', businessId)
      .select()
      .single();
    
    return { data, error };
  },

  removeUserFromBusiness: async (userId: string, businessId: string) => {
    const { data, error } = await supabase
      .from('user_business_memberships')
      .delete()
      .eq('user_id', userId)
      .eq('business_id', businessId);
    
    return { data, error };
  },

  getUserRoleInBusiness: async (businessId: string, userId?: string) => {
    const { data, error } = await supabase.rpc('get_user_role_in_business', {
      p_business_id: businessId,
      p_user_id: userId
    });
    
    return { data, error };
  }
};

// Invitation helper functions
export const invitationService = {
  createInvitation: async (businessId: string, email: string, role: 'admin' | 'manager' | 'staff') => {
    const { data, error } = await supabase.rpc('create_invitation', {
      p_business_id: businessId,
      p_email: email,
      p_role: role
    });
    return { data, error };
  },

  getBusinessInvitations: async (businessId: string) => {
    const { data, error } = await supabase
      .from('invitations')
      .select('*')
      .eq('business_id', businessId)
      .eq('used', false)
      .gt('expires_at', new Date().toISOString())
      .order('created_at', { ascending: false });
    
    return { data, error };
  },

  getInvitationByToken: async (token: string) => {
    const { data, error } = await supabase
      .from('invitations')
      .select(`
        *,
        business:businesses(name)
      `)
      .eq('token', token)
      .eq('used', false)
      .gt('expires_at', new Date().toISOString())
      .single();
    
    return { data, error };
  },

  acceptInvitation: async (token: string) => {
    const { data, error } = await supabase.rpc('accept_invitation', {
      p_token: token
    });
    return { data, error };
  },

  cancelInvitation: async (invitationId: string) => {
    const { data, error } = await supabase
      .from('invitations')
      .delete()
      .eq('id', invitationId);
    
    return { data, error };
  },

  resendInvitation: async (invitationId: string) => {
    const { data, error } = await supabase
      .from('invitations')
      .update({
        token: crypto.randomUUID(),
        expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
      })
      .eq('id', invitationId)
      .select()
      .single();
    
    return { data, error };
  }
};

// Customer helper functions
export const customerService = {
  getCustomers: async (businessId: string, options?: {
    search?: string;
    status?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    page?: number;
    limit?: number;
    pipelineStage?: string;
  }) => {
    let query = supabase
      .from('customers')
      .select('*', { count: 'exact' })
      .eq('business_id', businessId);

    // Apply search filter
    if (options?.search) {
      query = query.or(`name.ilike.%${options.search}%,email.ilike.%${options.search}%,company.ilike.%${options.search}%`);
    }

    // Apply status filter
    if (options?.status && options.status !== 'all') {
      query = query.eq('status', options.status);
    }

    // Apply pipeline stage filter
    if (options?.pipelineStage && options.pipelineStage !== 'all') {
      query = query.eq('pipeline_stage', options.pipelineStage);
    }

    // Apply sorting
    const sortBy = options?.sortBy || 'created_at';
    const sortOrder = options?.sortOrder || 'desc';
    query = query.order(sortBy, { ascending: sortOrder === 'asc' });

    // Apply pagination
    if (options?.page && options?.limit) {
      const from = (options.page - 1) * options.limit;
      const to = from + options.limit - 1;
      query = query.range(from, to);
    }

    const { data, error, count } = await query;
    return { data, error, count };
  },

  getCustomerById: async (customerId: string) => {
    const { data, error } = await supabase
      .from('customers')
      .select('*')
      .eq('id', customerId)
      .single();
    
    return { data, error };
  },

  createCustomer: async (customer: CustomerInsert) => {
    const { data, error } = await supabase
      .from('customers')
      .insert(customer)
      .select()
      .single();
    
    return { data, error };
  },

  createCustomers: async (customers: CustomerInsert[]) => {
    const { data, error } = await supabase
      .from('customers')
      .insert(customers)
      .select();
    
    return { data, error };
  },

  updateCustomer: async (customerId: string, updates: CustomerUpdate) => {
    const { data, error } = await supabase
      .from('customers')
      .update(updates)
      .eq('id', customerId)
      .select()
      .single();
    
    return { data, error };
  },

  updateCustomerPipelineStage: async (customerId: string, pipelineStage: string) => {
    const { data, error } = await supabase
      .from('customers')
      .update({ pipeline_stage: pipelineStage })
      .eq('id', customerId)
      .select()
      .single();
    
    return { data, error };
  },

  deleteCustomer: async (customerId: string) => {
    const { data, error } = await supabase
      .from('customers')
      .delete()
      .eq('id', customerId);
    
    return { data, error };
  },

  subscribeToCustomers: (businessId: string, callback: (payload: any) => void) => {
    return supabase
      .channel('customers')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'customers',
          filter: `business_id=eq.${businessId}`
        },
        callback
      )
      .subscribe();
  }
};

// Custom modules helper functions
export const customModuleService = {
  getModules: async (businessId: string) => {
    const { data, error } = await supabase
      .from('custom_modules')
      .select(`
        *,
        fields:form_fields(*)
      `)
      .eq('business_id', businessId)
      .order('created_at', { ascending: false });
    
    return { data, error };
  },

  getModuleById: async (moduleId: string) => {
    const { data, error } = await supabase
      .from('custom_modules')
      .select(`
        *,
        fields:form_fields(*)
      `)
      .eq('id', moduleId)
      .single();
    
    return { data, error };
  },

  createModule: async (module: CustomModuleInsert) => {
    const { data, error } = await supabase
      .from('custom_modules')
      .insert(module)
      .select()
      .single();
    
    return { data, error };
  },

  updateModule: async (moduleId: string, updates: CustomModuleUpdate) => {
    const { data, error } = await supabase
      .from('custom_modules')
      .update(updates)
      .eq('id', moduleId)
      .select()
      .single();
    
    return { data, error };
  },

  deleteModule: async (moduleId: string) => {
    const { data, error } = await supabase
      .from('custom_modules')
      .delete()
      .eq('id', moduleId);
    
    return { data, error };
  }
};

// Form fields helper functions
export const formFieldService = {
  getFields: async (moduleId: string) => {
    const { data, error } = await supabase
      .from('form_fields')
      .select('*')
      .eq('module_id', moduleId)
      .order('order_index', { ascending: true });
    
    return { data, error };
  },

  createField: async (field: FormFieldInsert) => {
    const { data, error } = await supabase
      .from('form_fields')
      .insert(field)
      .select()
      .single();
    
    return { data, error };
  },

  updateField: async (fieldId: string, updates: FormFieldUpdate) => {
    const { data, error } = await supabase
      .from('form_fields')
      .update(updates)
      .eq('id', fieldId)
      .select()
      .single();
    
    return { data, error };
  },

  deleteField: async (fieldId: string) => {
    const { data, error } = await supabase
      .from('form_fields')
      .delete()
      .eq('id', fieldId);
    
    return { data, error };
  },

  reorderFields: async (fields: { id: string; order_index: number }[]) => {
    const updates = fields.map(field => 
      supabase
        .from('form_fields')
        .update({ order_index: field.order_index })
        .eq('id', field.id)
    );
    
    const results = await Promise.all(updates);
    return results;
  },
  
  // New function to get dynamic options for select fields
  getDynamicOptions: async (
    businessId: string,
    sourceTable: string,
    valueColumn: string,
    labelColumn: string,
    filterColumn?: string,
    filterValue?: string
  ) => {
    try {
      let query = supabase
        .from(sourceTable)
        .select(`${valueColumn}, ${labelColumn}`)
        .eq('business_id', businessId);
      
      // Apply filter if provided
      if (filterColumn && filterValue) {
        query = query.eq(filterColumn, filterValue);
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      
      // Transform data to options format
      return data.map(item => ({
        value: item[valueColumn],
        label: item[labelColumn]
      }));
    } catch (error) {
      console.error('Error fetching dynamic options:', error);
      return [];
    }
  }
};

// Form data helper functions
export const formDataService = {
  getFormData: async (moduleId: string, options?: {
    search?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    page?: number;
    limit?: number;
  }) => {
    let query = supabase
      .from('form_data')
      .select('*', { count: 'exact' })
      .eq('module_id', moduleId);

    // Apply search filter (search in JSONB data)
    if (options?.search) {
      query = query.or(`data::text.ilike.%${options.search}%`);
    }

    // Apply sorting
    const sortBy = options?.sortBy || 'created_at';
    const sortOrder = options?.sortOrder || 'desc';
    query = query.order(sortBy, { ascending: sortOrder === 'asc' });

    // Apply pagination
    if (options?.page && options?.limit) {
      const from = (options.page - 1) * options.limit;
      const to = from + options.limit - 1;
      query = query.range(from, to);
    }

    const { data, error, count } = await query;
    return { data, error, count };
  },

  getFormDataById: async (dataId: string) => {
    const { data, error } = await supabase
      .from('form_data')
      .select('*')
      .eq('id', dataId)
      .single();
    
    return { data, error };
  },

  createFormData: async (formData: FormDataInsert) => {
    const { data, error } = await supabase
      .from('form_data')
      .insert(formData)
      .select()
      .single();
    
    return { data, error };
  },

  updateFormData: async (dataId: string, updates: FormDataUpdate) => {
    const { data, error } = await supabase
      .from('form_data')
      .update(updates)
      .eq('id', dataId)
      .select()
      .single();
    
    return { data, error };
  },

  deleteFormData: async (dataId: string) => {
    const { data, error } = await supabase
      .from('form_data')
      .delete()
      .eq('id', dataId);
    
    return { data, error };
  },

  validateFormData: async (moduleId: string, data: any) => {
    const { data: result, error } = await supabase.rpc('validate_form_data', {
      p_module_id: moduleId,
      p_data: data
    });
    
    return { data: result, error };
  },

  subscribeToFormData: (moduleId: string, callback: (payload: any) => void) => {
    return supabase
      .channel('form_data')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'form_data',
          filter: `module_id=eq.${moduleId}`
        },
        callback
      )
      .subscribe();
  }
};

// Role management helper functions
export const roleService = {
  getRoles: async (businessId: string) => {
    const { data, error } = await supabase
      .from('roles')
      .select(`
        *,
        user_roles(count)
      `)
      .eq('business_id', businessId)
      .order('created_at', { ascending: false });
    
    return { data, error };
  },

  getRoleById: async (roleId: string) => {
    const { data, error } = await supabase
      .from('roles')
      .select('*')
      .eq('id', roleId)
      .single();
    
    return { data, error };
  },

  createRole: async (role: RoleInsert) => {
    const { data, error } = await supabase
      .from('roles')
      .insert(role)
      .select()
      .single();
    
    return { data, error };
  },

  updateRole: async (roleId: string, updates: RoleUpdate) => {
    const { data, error } = await supabase
      .from('roles')
      .update(updates)
      .eq('id', roleId)
      .select()
      .single();
    
    return { data, error };
  },

  deleteRole: async (roleId: string) => {
    const { data, error } = await supabase
      .from('roles')
      .delete()
      .eq('id', roleId);
    
    return { data, error };
  },

  getUserRoles: async (userId: string, businessId: string) => {
    const { data, error } = await supabase
      .from('user_roles')
      .select(`
        *,
        role:roles(*)
      `)
      .eq('user_id', userId)
      .eq('business_id', businessId);
    
    return { data, error };
  },

  assignUserRole: async (userId: string, roleId: string, businessId: string) => {
    const { data, error } = await supabase.rpc('assign_user_role', {
      p_user_id: userId,
      p_role_id: roleId,
      p_business_id: businessId
    });
    
    return { data, error };
  },

  removeUserRole: async (userRoleId: string) => {
    const { data, error } = await supabase
      .from('user_roles')
      .delete()
      .eq('id', userRoleId);
    
    return { data, error };
  },

  getUserPermissions: async (userId: string, businessId: string) => {
    const { data, error } = await supabase.rpc('get_user_permissions', {
      p_user_id: userId,
      p_business_id: businessId
    });
    
    return { data, error };
  },

  checkUserPermission: async (userId: string, businessId: string, module: string, action: string) => {
    const { data, error } = await supabase.rpc('check_user_permission', {
      p_user_id: userId,
      p_business_id: businessId,
      p_module: module,
      p_action: action
    });
    
    return { data, error };
  }
};

// PDF Template helper functions
export const pdfTemplateService = {
  getTemplates: async (businessId: string, type?: string) => {
    let query = supabase
      .from('pdf_templates')
      .select('*')
      .eq('business_id', businessId);

    if (type) {
      query = query.eq('type', type);
    }

    query = query.order('created_at', { ascending: false });

    const { data, error } = await query;
    return { data, error };
  },

  getTemplateById: async (templateId: string) => {
    const { data, error } = await supabase
      .from('pdf_templates')
      .select('*')
      .eq('id', templateId)
      .single();
    
    return { data, error };
  },

  getDefaultTemplate: async (businessId: string, type: string) => {
    const { data, error } = await supabase
      .from('pdf_templates')
      .select('*')
      .eq('business_id', businessId)
      .eq('type', type)
      .eq('is_default', true)
      .single();
    
    return { data, error };
  },

  createTemplate: async (template: PDFTemplateInsert) => {
    const { data, error } = await supabase
      .from('pdf_templates')
      .insert(template)
      .select()
      .single();
    
    return { data, error };
  },

  updateTemplate: async (templateId: string, updates: PDFTemplateUpdate) => {
    const { data, error } = await supabase
      .from('pdf_templates')
      .update(updates)
      .eq('id', templateId)
      .select()
      .single();
    
    return { data, error };
  },

  deleteTemplate: async (templateId: string) => {
    const { data, error } = await supabase
      .from('pdf_templates')
      .delete()
      .eq('id', templateId);
    
    return { data, error };
  },

  setDefaultTemplate: async (templateId: string, businessId: string, templateType: string) => {
    const { data, error } = await supabase.rpc('set_default_pdf_template', {
      p_template_id: templateId,
      p_business_id: businessId,
      p_template_type: templateType
    });
    
    return { data, error };
  }
};

// Public Forms helper functions
export const publicFormService = {
  getPublicForms: async (businessId: string) => {
    const { data, error } = await supabase
      .from('public_forms')
      .select(`
        *,
        module:custom_modules(*)
      `)
      .eq('business_id', businessId)
      .order('created_at', { ascending: false });
    
    return { data, error };
  },

  getPublicFormById: async (formId: string) => {
    const { data, error } = await supabase
      .from('public_forms')
      .select(`
        *,
        module:custom_modules(*),
        business:businesses(*)
      `)
      .eq('id', formId)
      .single();
    
    return { data, error };
  },

  getPublicFormByUrl: async (publicUrl: string) => {
    const { data, error } = await supabase
      .from('public_forms')
      .select(`
        *,
        module:custom_modules(*),
        business:businesses(*)
      `)
      .eq('public_url', publicUrl)
      .eq('is_public', true)
      .single();
    
    return { data, error };
  },

  createPublicForm: async (moduleId: string, settings?: any) => {
    const { data, error } = await supabase.rpc('create_public_form', {
      p_module_id: moduleId,
      p_settings: settings
    });
    
    return { data, error };
  },

  updatePublicForm: async (formId: string, updates: PublicFormUpdate) => {
    const { data, error } = await supabase
      .from('public_forms')
      .update(updates)
      .eq('id', formId)
      .select()
      .single();
    
    return { data, error };
  },

  deletePublicForm: async (formId: string) => {
    const { data, error } = await supabase
      .from('public_forms')
      .delete()
      .eq('id', formId);
    
    return { data, error };
  },

  submitPublicForm: async (publicUrl: string, formData: any, submitterEmail?: string) => {
    const { data, error } = await supabase.rpc('submit_public_form', {
      p_public_url: publicUrl,
      p_form_data: formData,
      p_submitter_email: submitterEmail
    });
    
    return { data, error };
  }
};

// Notifications helper functions
export const notificationService = {
  getNotifications: async (businessId?: string, userId?: string) => {
    let query = supabase
      .from('notifications')
      .select('*')
      .order('created_at', { ascending: false });

    if (businessId) {
      query = query.eq('business_id', businessId);
    }

    if (userId) {
      query = query.eq('user_id', userId);
    }

    const { data, error } = await query;
    return { data, error };
  },

  getUnreadCount: async (businessId?: string, userId?: string) => {
    let query = supabase
      .from('notifications')
      .select('*', { count: 'exact', head: true })
      .eq('is_read', false);

    if (businessId) {
      query = query.eq('business_id', businessId);
    }

    if (userId) {
      query = query.eq('user_id', userId);
    }

    const { count, error } = await query;
    return { count: count || 0, error };
  },

  createNotification: async (
    businessId: string,
    userId: string,
    type: string,
    title: string,
    message: string,
    data?: any
  ) => {
    const { data: notificationData, error } = await supabase.rpc('create_notification', {
      p_business_id: businessId,
      p_user_id: userId,
      p_type: type,
      p_title: title,
      p_message: message,
      p_data: data || {}
    });
    
    return { data: notificationData, error };
  },

  markAsRead: async (notificationId: string) => {
    const { data, error } = await supabase.rpc('mark_notification_read', {
      p_notification_id: notificationId
    });
    
    return { data, error };
  },

  markAllAsRead: async (businessId?: string) => {
    const { data, error } = await supabase.rpc('mark_all_notifications_read', {
      p_business_id: businessId
    });
    
    return { data, error };
  },

  subscribeToNotifications: (businessId: string, callback: (payload: any) => void) => {
    return supabase
      .channel('notifications')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `business_id=eq.${businessId}`
        },
        callback
      )
      .subscribe();
  }
};

// HR Module helper functions
export const employeeService = {
  getEmployees: async (businessId: string, options?: {
    search?: string;
    department?: string;
    status?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    page?: number;
    limit?: number;
  }) => {
    let query = supabase
      .from('employees')
      .select('*', { count: 'exact' })
      .eq('business_id', businessId);

    // Apply search filter
    if (options?.search) {
      query = query.or(`full_name.ilike.%${options.search}%,email.ilike.%${options.search}%,position.ilike.%${options.search}%`);
    }

    // Apply department filter
    if (options?.department && options.department !== 'all') {
      query = query.eq('department', options.department);
    }

    // Apply status filter
    if (options?.status && options.status !== 'all') {
      query = query.eq('status', options.status);
    }

    // Apply sorting
    const sortBy = options?.sortBy || 'created_at';
    const sortOrder = options?.sortOrder || 'desc';
    query = query.order(sortBy, { ascending: sortOrder === 'asc' });

    // Apply pagination
    if (options?.page && options?.limit) {
      const from = (options.page - 1) * options.limit;
      const to = from + options.limit - 1;
      query = query.range(from, to);
    }

    const { data, error, count } = await query;
    return { data, error, count };
  },

  getEmployeeById: async (employeeId: string) => {
    const { data, error } = await supabase
      .from('employees')
      .select('*')
      .eq('id', employeeId)
      .single();
    
    return { data, error };
  },

  createEmployee: async (employee: EmployeeInsert) => {
    const { data, error } = await supabase
      .from('employees')
      .insert(employee)
      .select()
      .single();
    
    return { data, error };
  },

  createEmployees: async (employees: EmployeeInsert[]) => {
    const { data, error } = await supabase
      .from('employees')
      .insert(employees)
      .select();
    
    return { data, error };
  },

  updateEmployee: async (employeeId: string, updates: EmployeeUpdate) => {
    const { data, error } = await supabase
      .from('employees')
      .update(updates)
      .eq('id', employeeId)
      .select()
      .single();
    
    return { data, error };
  },

  deleteEmployee: async (employeeId: string) => {
    const { data, error } = await supabase
      .from('employees')
      .delete()
      .eq('id', employeeId);
    
    return { data, error };
  }
};

export const departmentService = {
  getDepartments: async (businessId: string) => {
    const { data, error } = await supabase
      .from('departments')
      .select(`
        *,
        manager:employees(id, full_name, position)
      `)
      .eq('business_id', businessId)
      .order('name', { ascending: true });
    
    return { data, error };
  },

  getDepartmentById: async (departmentId: string) => {
    const { data, error } = await supabase
      .from('departments')
      .select(`
        *,
        manager:employees(id, full_name, position)
      `)
      .eq('id', departmentId)
      .single();
    
    return { data, error };
  },

  createDepartment: async (department: DepartmentInsert) => {
    const { data, error } = await supabase
      .from('departments')
      .insert(department)
      .select()
      .single();
    
    return { data, error };
  },

  updateDepartment: async (departmentId: string, updates: DepartmentUpdate) => {
    const { data, error } = await supabase
      .from('departments')
      .update(updates)
      .eq('id', departmentId)
      .select()
      .single();
    
    return { data, error };
  },

  deleteDepartment: async (departmentId: string) => {
    const { data, error } = await supabase
      .from('departments')
      .delete()
      .eq('id', departmentId);
    
    return { data, error };
  },

  getDepartmentEmployees: async (departmentId: string) => {
    const { data, error } = await supabase
      .from('employees')
      .select('*')
      .eq('department', departmentId)
      .order('full_name', { ascending: true });
    
    return { data, error };
  }
};

// Finance Module helper functions
export const transactionService = {
  getTransactions: async (businessId: string, options?: {
    search?: string;
    type?: string;
    category?: string;
    dateFrom?: string;
    dateTo?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    page?: number;
    limit?: number;
  }) => {
    let query = supabase
      .from('transactions')
      .select('*', { count: 'exact' })
      .eq('business_id', businessId);

    // Apply search filter
    if (options?.search) {
      query = query.or(`description.ilike.%${options.search}%,reference.ilike.%${options.search}%,category.ilike.%${options.search}%`);
    }

    // Apply type filter
    if (options?.type && options.type !== 'all') {
      query = query.eq('type', options.type);
    }

    // Apply category filter
    if (options?.category && options.category !== 'all') {
      query = query.eq('category', options.category);
    }

    // Apply date range filter
    if (options?.dateFrom) {
      query = query.gte('date', options.dateFrom);
    }
    if (options?.dateTo) {
      query = query.lte('date', options.dateTo);
    }

    // Apply sorting
    const sortBy = options?.sortBy || 'date';
    const sortOrder = options?.sortOrder || 'desc';
    query = query.order(sortBy, { ascending: sortOrder === 'asc' });

    // Apply pagination
    if (options?.page && options?.limit) {
      const from = (options.page - 1) * options.limit;
      const to = from + options.limit - 1;
      query = query.range(from, to);
    }

    const { data, error, count } = await query;
    return { data, error, count };
  },

  getTransactionById: async (transactionId: string) => {
    const { data, error } = await supabase
      .from('transactions')
      .select('*')
      .eq('id', transactionId)
      .single();
    
    return { data, error };
  },

  createTransaction: async (transaction: TransactionInsert) => {
    // Get current user ID
    const { data: { user } } = await supabase.auth.getUser();
    
    // Add user_id to transaction data
    const transactionWithUser = {
      ...transaction,
      user_id: user?.id
    };
    
    const { data, error } = await supabase
      .from('transactions')
      .insert(transactionWithUser)
      .select()
      .single();
    
    return { data, error };
  },

  createTransactions: async (transactions: TransactionInsert[]) => {
    // Get current user ID
    const { data: { user } } = await supabase.auth.getUser();
    
    // Add user_id to all transactions
    const transactionsWithUser = transactions.map(transaction => ({
      ...transaction,
      user_id: user?.id
    }));
    
    const { data, error } = await supabase
      .from('transactions')
      .insert(transactionsWithUser)
      .select();
    
    return { data, error };
  },

  updateTransaction: async (transactionId: string, updates: TransactionUpdate) => {
    const { data, error } = await supabase
      .from('transactions')
      .update(updates)
      .eq('id', transactionId)
      .select()
      .single();
    
    return { data, error };
  },

  deleteTransaction: async (transactionId: string) => {
    const { data, error } = await supabase
      .from('transactions')
      .delete()
      .eq('id', transactionId);
    
    return { data, error };
  }
};

export const invoiceService = {
  getInvoices: async (businessId: string, options?: {
    search?: string;
    status?: string;
    customerId?: string;
    dateFrom?: string;
    dateTo?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    page?: number;
    limit?: number;
  }) => {
    let query = supabase
      .from('invoices')
      .select(`
        *,
        customer:customers(id, name, email, company)
      `, { count: 'exact' })
      .eq('business_id', businessId);

    // Apply search filter
    if (options?.search) {
      query = query.or(`customer.name.ilike.%${options.search}%,customer.email.ilike.%${options.search}%,customer.company.ilike.%${options.search}%`);
    }

    // Apply status filter
    if (options?.status && options.status !== 'all') {
      query = query.eq('status', options.status);
    }

    // Apply customer filter
    if (options?.customerId) {
      query = query.eq('customer_id', options.customerId);
    }

    // Apply date range filter
    if (options?.dateFrom) {
      query = query.gte('due_date', options.dateFrom);
    }
    if (options?.dateTo) {
      query = query.lte('due_date', options.dateTo);
    }

    // Apply sorting
    const sortBy = options?.sortBy || 'created_at';
    const sortOrder = options?.sortOrder || 'desc';
    query = query.order(sortBy, { ascending: sortOrder === 'asc' });

    // Apply pagination
    if (options?.page && options?.limit) {
      const from = (options.page - 1) * options.limit;
      const to = from + options.limit - 1;
      query = query.range(from, to);
    }

    const { data, error, count } = await query;
    return { data, error, count };
  },

  getInvoiceById: async (invoiceId: string) => {
    const { data, error } = await supabase
      .from('invoices')
      .select(`
        *,
        customer:customers(id, name, email, company, address, phone)
      `)
      .eq('id', invoiceId)
      .single();
    
    return { data, error };
  },

  createInvoice: async (invoice: InvoiceInsert) => {
    // Get current user ID
    const { data: { user } } = await supabase.auth.getUser();
    
    // Add user_id to invoice data
    const invoiceWithUser = {
      ...invoice,
      user_id: user?.id
    };
    
    const { data, error } = await supabase
      .from('invoices')
      .insert(invoiceWithUser)
      .select()
      .single();
    
    return { data, error };
  },

  updateInvoice: async (invoiceId: string, updates: InvoiceUpdate) => {
    const { data, error } = await supabase
      .from('invoices')
      .update(updates)
      .eq('id', invoiceId)
      .select()
      .single();
    
    return { data, error };
  },

  deleteInvoice: async (invoiceId: string) => {
    const { data, error } = await supabase
      .from('invoices')
      .delete()
      .eq('id', invoiceId);
    
    return { data, error };
  }
};

// Inventory Module helper functions
export const productService = {
  getProducts: async (businessId: string, options?: {
    search?: string;
    category?: string;
    lowStock?: boolean;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    page?: number;
    limit?: number;
  }) => {
    let query = supabase
      .from('products')
      .select('*', { count: 'exact' })
      .eq('business_id', businessId);

    // Apply search filter
    if (options?.search) {
      query = query.or(`name.ilike.%${options.search}%,sku.ilike.%${options.search}%,description.ilike.%${options.search}%`);
    }

    // Apply category filter
    if (options?.category && options.category !== 'all') {
      query = query.eq('category', options.category);
    }

    // Apply low stock filter
    if (options?.lowStock) {
      query = query.lt('stock_quantity', supabase.raw('low_stock_alert'));
    }

    // Apply sorting
    const sortBy = options?.sortBy || 'name';
    const sortOrder = options?.sortOrder || 'asc';
    query = query.order(sortBy, { ascending: sortOrder === 'asc' });

    // Apply pagination
    if (options?.page && options?.limit) {
      const from = (options.page - 1) * options.limit;
      const to = from + options.limit - 1;
      query = query.range(from, to);
    }

    const { data, error, count } = await query;
    return { data, error, count };
  },

  getProductById: async (productId: string) => {
    const { data, error } = await supabase
      .from('products')
      .select('*')
      .eq('id', productId)
      .single();
    
    return { data, error };
  },

  createProduct: async (product: ProductInsert) => {
    const { data, error } = await supabase
      .from('products')
      .insert(product)
      .select()
      .single();
    
    return { data, error };
  },

  createProducts: async (products: ProductInsert[]) => {
    const { data, error } = await supabase
      .from('products')
      .insert(products)
      .select();
    
    return { data, error };
  },

  updateProduct: async (productId: string, updates: ProductUpdate) => {
    const { data, error } = await supabase
      .from('products')
      .update(updates)
      .eq('id', productId)
      .select()
      .single();
    
    return { data, error };
  },

  deleteProduct: async (productId: string) => {
    const { data, error } = await supabase
      .from('products')
      .delete()
      .eq('id', productId);
    
    return { data, error };
  }
};

export const supplierService = {
  getSuppliers: async (businessId: string, options?: {
    search?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    page?: number;
    limit?: number;
  }) => {
    let query = supabase
      .from('suppliers')
      .select('*', { count: 'exact' })
      .eq('business_id', businessId);

    // Apply search filter
    if (options?.search) {
      query = query.or(`name.ilike.%${options.search}%,email.ilike.%${options.search}%,phone.ilike.%${options.search}%`);
    }

    // Apply sorting
    const sortBy = options?.sortBy || 'name';
    const sortOrder = options?.sortOrder || 'asc';
    query = query.order(sortBy, { ascending: sortOrder === 'asc' });

    // Apply pagination
    if (options?.page && options?.limit) {
      const from = (options.page - 1) * options.limit;
      const to = from + options.limit - 1;
      query = query.range(from, to);
    }

    const { data, error, count } = await query;
    return { data, error, count };
  },

  getSupplierById: async (supplierId: string) => {
    const { data, error } = await supabase
      .from('suppliers')
      .select('*')
      .eq('id', supplierId)
      .single();
    
    return { data, error };
  },

  createSupplier: async (supplier: SupplierInsert) => {
    const { data, error } = await supabase
      .from('suppliers')
      .insert(supplier)
      .select()
      .single();
    
    return { data, error };
  },

  updateSupplier: async (supplierId: string, updates: SupplierUpdate) => {
    const { data, error } = await supabase
      .from('suppliers')
      .update(updates)
      .eq('id', supplierId)
      .select()
      .single();
    
    return { data, error };
  },

  deleteSupplier: async (supplierId: string) => {
    const { data, error } = await supabase
      .from('suppliers')
      .delete()
      .eq('id', supplierId);
    
    return { data, error };
  }
};

// POS Module helper functions
export const saleService = {
  getSales: async (businessId: string, options?: {
    search?: string;
    status?: string;
    customerId?: string;
    dateFrom?: string;
    dateTo?: string;
    paymentMethod?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    page?: number;
    limit?: number;
  }) => {
    let query = supabase
      .from('sales')
      .select(`
        *,
        customer:customers(id, name, email, company),
        user:user_profiles(id, full_name)
      `, { count: 'exact' })
      .eq('business_id', businessId);

    // Apply search filter
    if (options?.search) {
      query = query.or(`customer.name.ilike.%${options.search}%,customer.email.ilike.%${options.search}%,customer.company.ilike.%${options.search}%`);
    }

    // Apply status filter
    if (options?.status && options.status !== 'all') {
      query = query.eq('status', options.status);
    }

    // Apply customer filter
    if (options?.customerId) {
      query = query.eq('customer_id', options.customerId);
    }

    // Apply payment method filter
    if (options?.paymentMethod && options.paymentMethod !== 'all') {
      query = query.eq('payment_method', options.paymentMethod);
    }

    // Apply date range filter
    if (options?.dateFrom) {
      query = query.gte('date', options.dateFrom);
    }
    if (options?.dateTo) {
      query = query.lte('date', options.dateTo);
    }

    // Apply sorting
    const sortBy = options?.sortBy || 'date';
    const sortOrder = options?.sortOrder || 'desc';
    query = query.order(sortBy, { ascending: sortOrder === 'asc' });

    // Apply pagination
    if (options?.page && options?.limit) {
      const from = (options.page - 1) * options.limit;
      const to = from + options.limit - 1;
      query = query.range(from, to);
    }

    const { data, error, count } = await query;
    return { data, error, count };
  },

  getSaleById: async (saleId: string) => {
    const { data, error } = await supabase
      .from('sales')
      .select(`
        *,
        customer:customers(id, name, email, company, address, phone)
      `)
      .eq('id', saleId)
      .single();
    
    return { data, error };
  },

  createSale: async (sale: SaleInsert) => {
    // Get current user ID
    const { data: { user } } = await supabase.auth.getUser();
    
    // Add user_id to sale data
    const saleWithUser = {
      ...sale,
      user_id: user?.id
    };
    
    const { data, error } = await supabase
      .from('sales')
      .insert(saleWithUser)
      .select()
      .single();
    
    return { data, error };
  },

  updateSale: async (saleId: string, updates: SaleUpdate) => {
    const { data, error } = await supabase
      .from('sales')
      .update(updates)
      .eq('id', saleId)
      .select()
      .single();
    
    return { data, error };
  },

  deleteSale: async (saleId: string) => {
    const { data, error } = await supabase
      .from('sales')
      .delete()
      .eq('id', saleId);
    
    return { data, error };
  }
};

// Payroll Module helper functions
export const payrollService = {
  getPayrollRecords: async (businessId: string, options?: {
    search?: string;
    status?: string;
    employeeId?: string;
    dateFrom?: string;
    dateTo?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    page?: number;
    limit?: number;
  }) => {
    let query = supabase
      .from('payroll_records')
      .select(`
        *,
        employee:employees(id, full_name, position, department)
      `, { count: 'exact' })
      .eq('business_id', businessId);

    // Apply employee filter
    if (options?.employeeId) {
      query = query.eq('employee_id', options.employeeId);
    }

    // Apply status filter
    if (options?.status && options.status !== 'all') {
      query = query.eq('status', options.status);
    }

    // Apply date range filter
    if (options?.dateFrom) {
      query = query.gte('pay_period_start', options.dateFrom);
    }
    if (options?.dateTo) {
      query = query.lte('pay_period_end', options.dateTo);
    }

    // Apply sorting
    const sortBy = options?.sortBy || 'created_at';
    const sortOrder = options?.sortOrder || 'desc';
    query = query.order(sortBy, { ascending: sortOrder === 'asc' });

    // Apply pagination
    if (options?.page && options?.limit) {
      const from = (options.page - 1) * options.limit;
      const to = from + options.limit - 1;
      query = query.range(from, to);
    }

    const { data, error, count } = await query;
    return { data, error, count };
  },

  getPayrollById: async (payrollId: string) => {
    const { data, error } = await supabase
      .from('payroll_records')
      .select(`
        *,
        employee:employees(id, full_name, position, department, email)
      `)
      .eq('id', payrollId)
      .single();
    
    return { data, error };
  },

  createPayroll: async (payroll: PayrollRecordInsert) => {
    const { data, error } = await supabase
      .from('payroll_records')
      .insert(payroll)
      .select()
      .single();
    
    return { data, error };
  },

  updatePayroll: async (payrollId: string, updates: PayrollRecordUpdate) => {
    const { data, error } = await supabase
      .from('payroll_records')
      .update(updates)
      .eq('id', payrollId)
      .select()
      .single();
    
    return { data, error };
  },

  deletePayroll: async (payrollId: string) => {
    const { data, error } = await supabase
      .from('payroll_records')
      .delete()
      .eq('id', payrollId);
    
    return { data, error };
  }
};

// Leave Module helper functions
export const leaveService = {
  getLeaveRequests: async (businessId: string, options?: {
    search?: string;
    status?: string;
    leaveType?: string;
    employeeId?: string;
    dateFrom?: string;
    dateTo?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    page?: number;
    limit?: number;
  }) => {
    let query = supabase
      .from('leave_requests')
      .select(`
        *,
        employee:employees(id, full_name, position, department),
        requester:users!requested_by(id, email),
        approver:users!approved_by(id, email)
      `, { count: 'exact' })
      .eq('business_id', businessId);

    // Apply employee filter
    if (options?.employeeId) {
      query = query.eq('employee_id', options.employeeId);
    }

    // Apply status filter
    if (options?.status && options.status !== 'all') {
      query = query.eq('status', options.status);
    }

    // Apply leave type filter
    if (options?.leaveType && options.leaveType !== 'all') {
      query = query.eq('leave_type', options.leaveType);
    }

    // Apply date range filter
    if (options?.dateFrom) {
      query = query.gte('start_date', options.dateFrom);
    }
    if (options?.dateTo) {
      query = query.lte('end_date', options.dateTo);
    }

    // Apply sorting
    const sortBy = options?.sortBy || 'created_at';
    const sortOrder = options?.sortOrder || 'desc';
    query = query.order(sortBy, { ascending: sortOrder === 'asc' });

    // Apply pagination
    if (options?.page && options?.limit) {
      const from = (options.page - 1) * options.limit;
      const to = from + options.limit - 1;
      query = query.range(from, to);
    }

    const { data, error, count } = await query;
    return { data, error, count };
  },

  getLeaveRequestById: async (leaveId: string) => {
    const { data, error } = await supabase
      .from('leave_requests')
      .select(`
        *,
        employee:employees(id, full_name, position, department, email),
        requester:users!requested_by(id, email),
        approver:users!approved_by(id, email)
      `)
      .eq('id', leaveId)
      .single();
    
    return { data, error };
  },

  createLeaveRequest: async (leave: LeaveRequestInsert) => {
    const { data, error } = await supabase
      .from('leave_requests')
      .insert(leave)
      .select()
      .single();
    
    return { data, error };
  },

  updateLeaveRequest: async (leaveId: string, updates: LeaveRequestUpdate) => {
    const { data, error } = await supabase
      .from('leave_requests')
      .update(updates)
      .eq('id', leaveId)
      .select()
      .single();
    
    return { data, error };
  },

  deleteLeaveRequest: async (leaveId: string) => {
    const { data, error } = await supabase
      .from('leave_requests')
      .delete()
      .eq('id', leaveId);
    
    return { data, error };
  },

  approveLeaveRequest: async (leaveId: string, approverId: string) => {
    const { data, error } = await supabase
      .from('leave_requests')
      .update({
        status: 'approved',
        approved_by: approverId,
        approval_date: new Date().toISOString()
      })
      .eq('id', leaveId)
      .select()
      .single();
    
    return { data, error };
  },

  rejectLeaveRequest: async (leaveId: string, approverId: string) => {
    const { data, error } = await supabase
      .from('leave_requests')
      .update({
        status: 'rejected',
        approved_by: approverId,
        approval_date: new Date().toISOString()
      })
      .eq('id', leaveId)
      .select()
      .single();
    
    return { data, error };
  }
};